// SKILLSMITH-CONTROL (Andrei, 2026-11-02): Skillsmith is the super admin's remote control, one per
// client. `/operator/skillsmith` lists the clients the super admin operates; picking one opens
// `/operator/skillsmith/{company}`: that client's Skillsmith thread (every order and its answer,
// oldest first) and a composer that gives an order now, once at a set time, weekly or monthly,
// with the standing orders below it (pause, resume, cancel). Every run reads that client only and
// is billed to it as "System usage (maintenance)". Operator console only: the API answers 404 to
// anyone but a super admin, and while the SKILLSMITH_LENS / SKILLSMITH_CONTROL flags are off.
import {
  Badge,
  Button,
  EmptyState,
  Input,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Notice,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { WrenchIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { LinkButton, OpsBody, OpsFrame, operatorCrumb, RowSub, TABLE_ROW_HOVER } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

const keys = {
  companies: ["operator", "skillsmith", "companies"] as const,
  orders: (co: string) => ["operator", "skillsmith", "orders", co] as const,
  standing: (co: string) => ["operator", "skillsmith", "standing", co] as const,
};

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

const crumb = { label: "Skillsmith", render: <Link to="/operator/skillsmith" /> };

/** `/operator/skillsmith`: pick a client to open its Skillsmith. */
export function SkillsmithClientsPage({ api }: { api: Api }) {
  const q = useQuery({
    queryKey: keys.companies,
    queryFn: () => api.client.operator.skillsmith.companies.list(),
  });
  const rows = q.data?.data ?? [];
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, { label: "Skillsmith" }]}
        icon={WrenchIcon}
        title="Skillsmith"
        count={q.isPending ? undefined : rows.length}
        actions={<Badge variant="outline">Private · super admins</Badge>}
      />
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading clients" />
        ) : q.isError ? (
          <LoadError what="Clients" error={q.error} />
        ) : rows.length === 0 ? (
          <EmptyState
            variant="card"
            icon={WrenchIcon}
            title="No clients to open"
            description="A client appears here once the platform operates it."
          />
        ) : (
          <Table data-slot="skillsmith-clients">
            <TableCaption>
              Each client has its own Skillsmith. It reads that client only and runs on that
              client's usage, billed as System usage (maintenance).
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Client</TableHead>
                <TableHead scope="col">Last 7 days</TableHead>
                <TableHead scope="col">
                  <span className="sr-only">Open</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.company_id} data-client={r.company_id} className={TABLE_ROW_HOVER}>
                  <TableHead scope="row" className="font-normal">
                    <span className="font-medium">{r.name}</span>
                    <RowSub>{r.slug}</RowSub>
                  </TableHead>
                  <TableCell>
                    {r.runs_7d} runs · {r.failed_7d} failed · {r.open_alerts} open alerts
                  </TableCell>
                  <TableCell className="text-right">
                    <LinkButton
                      size="xs"
                      render={
                        <Link
                          to="/operator/skillsmith/$companyId"
                          params={{ companyId: r.company_id }}
                        />
                      }
                    >
                      Open Skillsmith
                    </LinkButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </OpsBody>
    </OpsFrame>
  );
}

type Cadence = "now" | "once" | "weekly" | "monthly";

/** `/operator/skillsmith/{company}`: one client's Skillsmith thread, composer and standing orders. */
export function SkillsmithClientPage({ api, companyId }: { api: Api; companyId: string }) {
  const client = useQueryClient();
  const companies = useQuery({
    queryKey: keys.companies,
    queryFn: () => api.client.operator.skillsmith.companies.list(),
  });
  const orders = useQuery({
    queryKey: keys.orders(companyId),
    queryFn: () =>
      api.client.operator.skillsmith.orders.list({ params: { company_id: companyId } }),
  });
  const standing = useQuery({
    queryKey: keys.standing(companyId),
    queryFn: () =>
      api.client.operator.skillsmith.standingOrders.list({ params: { company_id: companyId } }),
  });
  const [text, setText] = useState("");
  const [cadence, setCadence] = useState<Cadence>("now");
  const [runAt, setRunAt] = useState("");
  const [at, setAt] = useState("07:00");
  const [weekday, setWeekday] = useState(1);
  const [monthDay, setMonthDay] = useState(1);
  const refresh = () => {
    void client.invalidateQueries({ queryKey: keys.orders(companyId) });
    void client.invalidateQueries({ queryKey: keys.standing(companyId) });
  };
  const give = useMutation({
    mutationFn: async () => {
      const body = text.trim();
      if (cadence === "now")
        return api.client.operator.skillsmith.orders.create({
          params: { company_id: companyId },
          body: { text: body, kind: "order" },
        });
      return api.client.operator.skillsmith.standingOrders.create({
        params: { company_id: companyId },
        body:
          cadence === "once"
            ? { text: body, cadence, ...(runAt ? { run_at: new Date(runAt).toISOString() } : {}) }
            : cadence === "weekly"
              ? { text: body, cadence, at, weekday }
              : { text: body, cadence, at, month_day: monthDay },
      });
    },
    onSuccess: () => {
      setText("");
      refresh();
    },
  });
  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: "active" | "paused" | "cancelled" }) =>
      api.client.operator.skillsmith.standingOrders.status({
        params: { company_id: companyId, standing_order_id: v.id },
        body: { status: v.status },
      }),
    onSuccess: refresh,
  });
  const name = companies.data?.data.find((c) => c.company_id === companyId)?.name ?? "Client";
  const thread = [...(orders.data?.data ?? [])].reverse();
  const live = (standing.data?.data ?? []).filter(
    (s) => s.status === "active" || s.status === "paused",
  );
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={[operatorCrumb, crumb, { label: name }]}
        icon={WrenchIcon}
        title={`Skillsmith · ${name}`}
        actions={<Badge variant="outline">Private · super admins</Badge>}
      />
      <OpsBody>
        <div className="flex flex-col gap-6" data-slot="skillsmith-client">
          <RowSub>
            Reads {name} only. Every order runs on {name}'s usage, billed as System usage
            (maintenance). Nothing is changed without your click.
          </RowSub>
          {orders.isPending ? (
            <Loading label="Loading the thread" />
          ) : orders.isError ? (
            <LoadError what="The thread" error={orders.error} />
          ) : thread.length === 0 ? (
            <EmptyState
              variant="card"
              icon={WrenchIcon}
              title="No orders yet"
              description="Give it an order below: now, once at a set time, weekly or monthly."
            />
          ) : (
            <ol className="flex flex-col gap-4" aria-label="Orders">
              {thread.map((o) => (
                <li key={o.id} className="flex flex-col gap-1" data-order={o.id}>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">You</span>
                    {o.standing_order_id ? <Badge variant="outline">Scheduled</Badge> : null}
                    <RowSub>{when(o.created_at)}</RowSub>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{o.text}</p>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Skillsmith</span>
                    <Badge variant={o.status === "answered" ? "success" : "warning"}>
                      {o.status}
                    </Badge>
                    <RowSub>
                      {o.tool_calls.length} reads · ${(o.cost_micros / 1_000_000).toFixed(4)}{" "}
                      maintenance
                    </RowSub>
                  </div>
                  <p className="whitespace-pre-wrap text-sm">{o.answer ?? "Working on it…"}</p>
                </li>
              ))}
            </ol>
          )}
          <form
            className="flex flex-col gap-2"
            aria-label="Give an order"
            onSubmit={(e) => {
              e.preventDefault();
              if (text.trim()) give.mutate();
            }}
          >
            <Textarea
              aria-label={`Order for ${name}'s Skillsmith`}
              placeholder="What failed this week, and why?"
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
            <div className="flex flex-wrap items-center gap-2">
              <NativeSelect
                aria-label="When"
                value={cadence}
                onChange={(e) => setCadence(e.target.value as Cadence)}
              >
                <NativeSelectOption value="now">Now</NativeSelectOption>
                <NativeSelectOption value="once">Once, at a set time</NativeSelectOption>
                <NativeSelectOption value="weekly">Weekly</NativeSelectOption>
                <NativeSelectOption value="monthly">Monthly</NativeSelectOption>
              </NativeSelect>
              {cadence === "once" ? (
                <Input
                  type="datetime-local"
                  aria-label="Run at"
                  value={runAt}
                  onChange={(e) => setRunAt(e.target.value)}
                />
              ) : null}
              {cadence === "weekly" ? (
                <NativeSelect
                  aria-label="Weekday"
                  value={String(weekday)}
                  onChange={(e) => setWeekday(Number(e.target.value))}
                >
                  {DAYS.map((d, i) => (
                    <NativeSelectOption key={d} value={String(i + 1)}>
                      {d}
                    </NativeSelectOption>
                  ))}
                </NativeSelect>
              ) : null}
              {cadence === "monthly" ? (
                <Input
                  type="number"
                  min={1}
                  max={28}
                  aria-label="Day of the month"
                  value={monthDay}
                  onChange={(e) => setMonthDay(Number(e.target.value))}
                />
              ) : null}
              {cadence === "weekly" || cadence === "monthly" ? (
                <Input
                  type="time"
                  aria-label="Time of day"
                  value={at}
                  onChange={(e) => setAt(e.target.value)}
                />
              ) : null}
              <Button type="submit" size="sm" disabled={!text.trim() || give.isPending}>
                {cadence === "now" ? "Give order" : "Schedule"}
              </Button>
            </div>
            {give.isError ? (
              <Notice tone="danger" role="alert">
                The order was not taken: {give.error.message}
              </Notice>
            ) : null}
          </form>
          <section aria-label="Standing orders" className="flex flex-col gap-2">
            <h2 className="font-medium">Standing orders</h2>
            {standing.isPending ? (
              <Loading label="Loading standing orders" />
            ) : standing.isError ? (
              <LoadError what="Standing orders" error={standing.error} />
            ) : live.length === 0 ? (
              <RowSub>None. Pick "Once", "Weekly" or "Monthly" above to leave one.</RowSub>
            ) : (
              <ul className="flex flex-col gap-2">
                {live.map((s) => (
                  <li key={s.id} className="flex flex-wrap items-center gap-2" data-standing={s.id}>
                    <Badge variant={s.status === "active" ? "success" : "neutral"}>
                      {s.cadence === "once"
                        ? "Once"
                        : s.cadence === "weekly"
                          ? `Weekly · ${DAYS[(s.weekday ?? 1) - 1]} ${s.at}`
                          : `Monthly · day ${s.month_day} ${s.at}`}
                    </Badge>
                    <span className="text-sm">{s.text}</span>
                    <RowSub>
                      {s.status === "paused"
                        ? "paused"
                        : s.next_run_at
                          ? `next ${when(s.next_run_at)}`
                          : ""}
                      {s.runs ? ` · ran ${s.runs}×` : ""}
                      {s.last_note ? ` · ${s.last_note}` : ""}
                    </RowSub>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate({
                          id: s.id,
                          status: s.status === "active" ? "paused" : "active",
                        })
                      }
                    >
                      {s.status === "active" ? "Pause" : "Resume"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={setStatus.isPending}
                      onClick={() => setStatus.mutate({ id: s.id, status: "cancelled" })}
                    >
                      Cancel
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </OpsBody>
    </OpsFrame>
  );
}
